import React from 'react'

import { cva, type VariantProps } from 'class-variance-authority'

import { Icons } from '@/components/atoms/Icons/Icons.tsx'
import { WaitTimeToggle } from '@/components/atoms/WaitTimeToggle/WaitTimeToggle.tsx'
import { cn } from '@/lib/utils.ts'

const waitTimeVariants = cva(
  'inline-flex items-center gap-8 rounded-allocated border border-solid border-gray-600 bg-black pl-2',
  {
    variants: {
      state: {
        default: '',
        exceedsThreshold: '',
        addMore: '',
      },
    },
    defaultVariants: {
      state: 'default',
    },
  }
)

const delayVariants = cva('flex items-center gap-2 text-base', {
  variants: {
    state: {
      default: 'text-green-400 *:fill-green-400',
      exceedsThreshold: 'text-orange-400 *:fill-orange-400',
      addMore: 'text-red-500 *:fill-red-500',
    },
    hover: {
      true: 'text-white *:fill-white',
      false: '',
    },
  },
  defaultVariants: {
    state: 'default',
  },
})

export interface WaitTimeLabels {
  increase: string
  automate: string
}

export interface WaitTimeProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof waitTimeVariants> {
  delay: string
  labels: WaitTimeLabels
}

export const WaitTime = React.forwardRef<HTMLButtonElement, WaitTimeProps>(
  ({ className, state = 'default', delay, labels, ...props }, ref) => {
    const [hover, setHover] = React.useState(false)

    return (
      <div className={cn(waitTimeVariants({ state, className }))}>
        <div className={cn(delayVariants({ state, hover }))}>
          <Icons.watch className="size-[17px]" />
          <div className="min-w-[110px] text-left whitespace-nowrap">
            <p className={cn('font-bold select-none', hover && 'hidden')}>{delay}</p>
            <p className={cn('hidden font-normal italic select-none', hover && 'block')}>
              {state === 'default' ? labels.increase : labels.automate}
            </p>
          </div>
        </div>

        {props.disabled ? (
          <div className="h-[33px]" />
        ) : (
          <WaitTimeToggle
            state={state}
            ref={ref}
            onMouseEnter={() => {
              setHover(true)
            }}
            onMouseLeave={() => {
              setHover(false)
            }}
            {...props}
          />
        )}
      </div>
    )
  }
)
